import { getLocalVariantsWithProductData } from "@bcc/components/products/products-table/products-table.utils";
import type { VariantWithProductData } from "@bcc/components/products/products-table/types";
import { zoneFromPosition } from "@shared/lib/intensity/intensity-estimator";
import type { FormProduct, FormVariant, Installation, IntensityPosition, Product, ProductConnection, ProductImage } from "@shared/types";

export interface DemoProductsOptions {
  /** Cross-border roasters ship to several countries; their rows carry a ";"-separated ship-to like the real SKUs */
  shipTo?: readonly string[];
}

interface DemoVariantSeed {
  bagSize: string;
  grind: string[];
  webStatus: FormVariant["webStatus"];
  wholesalePrice: number;
  price: number;
  subscribers: number;
  weeklyLimit: number;
  inventory: number;
}

interface DemoProductSeed {
  title: string;
  productType: string;
  description: string;
  intensityPosition: IntensityPosition;
  variants: DemoVariantSeed[];
}

const DEMO_IMAGE_COLORS = ["#8b5e3c", "#6f4e37", "#a67b5b", "#4b3621"] as const;

/** Inline SVG bag image, no network request */
const buildDemoImages = (title: string, productIndex: number): ProductImage[] => {
  const color = DEMO_IMAGE_COLORS[productIndex % DEMO_IMAGE_COLORS.length];
  const initials = title
    .split(" ")
    .map((word) => word.charAt(0))
    .join("")
    .slice(0, 2)
    .toUpperCase();
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="96" height="96"><rect width="96" height="96" rx="8" fill="${color}"/><rect x="28" y="16" width="40" height="64" rx="6" fill="#f5ebe0"/><text x="48" y="54" font-size="16" font-family="sans-serif" text-anchor="middle" fill="${color}">${initials}</text></svg>`;
  return [{ src: `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`, width: 96, height: 96 }];
};

// A mix of live, out-of-stock and not-visible SKUs so the status filter and badges have something to show
const DEMO_PRODUCT_SEEDS: readonly DemoProductSeed[] = [
  {
    title: "Single Origin Ethiopia",
    productType: "Single Origin",
    description: "Bright and fruity with floral notes",
    intensityPosition: 2,
    variants: [
      {
        bagSize: "340g",
        grind: ["Whole Bean"],
        webStatus: "Visible for Sale",
        wholesalePrice: 14,
        price: 19,
        subscribers: 21,
        weeklyLimit: 100,
        inventory: 500,
      },
      {
        bagSize: "1kg",
        grind: ["Whole Bean"],
        webStatus: "Visible for Sale",
        wholesalePrice: 36,
        price: 48,
        subscribers: 9,
        weeklyLimit: 40,
        inventory: 120,
      },
    ],
  },
  {
    title: "House Blend",
    productType: "Blend",
    description: "Balanced and chocolatey, made for every day",
    intensityPosition: 5,
    variants: [
      {
        bagSize: "340g",
        grind: ["Whole Bean", "Espresso"],
        webStatus: "Visible for Sale",
        wholesalePrice: 12,
        price: 16,
        subscribers: 54,
        weeklyLimit: 150,
        inventory: 800,
      },
      {
        bagSize: "1kg",
        grind: ["Espresso"],
        webStatus: "Out of Stock",
        wholesalePrice: 32,
        price: 42,
        subscribers: 12,
        weeklyLimit: 25,
        inventory: 0,
      },
    ],
  },
  {
    title: "Decaf Colombia",
    productType: "Single Origin",
    description: "Smooth and sweet with caramel notes",
    intensityPosition: 4,
    variants: [
      {
        bagSize: "250g",
        grind: ["Whole Bean", "Filter"],
        webStatus: "Visible for Sale",
        wholesalePrice: 10,
        price: 14,
        subscribers: 6,
        weeklyLimit: 60,
        inventory: 240,
      },
    ],
  },
  {
    title: "Seasonal Reserve",
    productType: "Single Origin",
    description: "Limited release, not yet on sale",
    intensityPosition: 3,
    variants: [
      {
        bagSize: "340g",
        grind: ["Whole Bean"],
        webStatus: "Not Visible",
        wholesalePrice: 18,
        price: 24,
        subscribers: 0,
        weeklyLimit: 30,
        inventory: 90,
      },
    ],
  },
];

/** Sample catalogue in the same shape the products page builds from PIM, so it goes through the real table pipeline */
export const buildDemoFormProducts = ({ shipTo = [] }: DemoProductsOptions = {}): FormProduct[] => {
  const shipToValue = shipTo.length > 1 ? shipTo.join(";") : undefined;
  return DEMO_PRODUCT_SEEDS.map((seed, productIndex) => ({
    type: "product",
    id: `demo-product-${productIndex + 1}`,
    title: seed.title,
    description: seed.description,
    productType: seed.productType,
    images: buildDemoImages(seed.title, productIndex),
    isProductFormValid: true,
    intensityPosition: seed.intensityPosition,
    intensityZone: zoneFromPosition(seed.intensityPosition),
    ...(shipToValue ? { ship_to: shipToValue } : {}),
    variants: seed.variants.map((variant, variantIndex) => ({
      type: "variant",
      id: `demo-variant-${productIndex + 1}-${variantIndex + 1}`,
      bagSize: variant.bagSize,
      webStatus: variant.webStatus,
      grind: variant.grind,
      wholesalePrice: variant.wholesalePrice,
      price: variant.price,
      activeSubscriberCount: variant.subscribers,
      maxBagsShippedPerWeek: variant.weeklyLimit,
      weeklyInventory: variant.weeklyLimit,
      inventoryQuantity: variant.inventory,
      term: "Long",
    })),
  }));
};

/** Flat table rows for the sample catalogue, produced by the same function the real page uses */
export const buildDemoVariantData = (options: DemoProductsOptions = {}): VariantWithProductData[] =>
  getLocalVariantsWithProductData(buildDemoFormProducts(options));

/**
 * Sample store behind the tour's Map Products and Add Product dialogs: an active installation plus the
 * roaster's catalogue, the store's products and the (empty) connections between them.
 */
export interface DemoStore {
  installation: Installation;
  localProducts: Product[];
  remoteProducts: Product[];
  remoteFormProducts: FormProduct[];
  connections: ProductConnection[];
}

export interface DemoStoreOptions {
  vendorKey: string;
}

/** Beanz-side catalogue the roaster maps from; same products and bag sizes as the table's sample rows */
const buildDemoLocalProducts = (): Product[] =>
  DEMO_PRODUCT_SEEDS.map((seed, productIndex) => ({
    type: "product",
    id: `demo-product-${productIndex + 1}`,
    title: seed.title,
    description: seed.description,
    productType: seed.productType,
    images: buildDemoImages(seed.title, productIndex),
    variants: seed.variants.map((variant, variantIndex) => ({
      type: "variant",
      id: `demo-variant-${productIndex + 1}-${variantIndex + 1}`,
      bagSize: variant.bagSize,
      webStatus: variant.webStatus,
      grind: variant.grind,
      price: variant.price,
      wholesalePrice: variant.wholesalePrice,
      term: "Long",
    })),
  }));

/** The roaster's store listing of the same coffees, one store variant per bag size */
const buildDemoRemoteProducts = (): Product[] =>
  DEMO_PRODUCT_SEEDS.map((seed, productIndex) => ({
    type: "product",
    id: `demo-store-product-${productIndex + 1}`,
    title: seed.title,
    productType: seed.productType,
    images: buildDemoImages(seed.title, productIndex),
    variants: seed.variants.map((variant, variantIndex) => ({
      type: "variant",
      id: `demo-store-variant-${productIndex + 1}-${variantIndex + 1}`,
      title: variant.bagSize,
      bagSize: variant.bagSize,
      price: variant.price,
    })),
  }));

const toRemoteFormProduct = (product: Product): FormProduct => ({
  type: "product",
  id: product.id,
  title: product.title,
  productType: product.productType ?? "Coffee",
  images: product.images ?? [],
  isProductFormValid: true,
  variants: (product.variants ?? []).map((variant) => ({
    type: "variant",
    id: variant.id,
    title: variant.title,
    bagSize: variant.bagSize ?? "",
    price: variant.price,
  })),
});

export const buildDemoStore = ({ vendorKey }: DemoStoreOptions): DemoStore => {
  const remoteProducts = buildDemoRemoteProducts();
  return {
    installation: {
      id: "demo-installation",
      type: "installation",
      vendorKey,
      serviceId: "shopify",
      identifier: "demo-store",
      domain: "demo-store.myshopify.com",
      state: "ACTIVE",
    },
    localProducts: buildDemoLocalProducts(),
    remoteProducts,
    remoteFormProducts: remoteProducts.map(toRemoteFormProduct),
    connections: [],
  };
};
